/** Enter my car's mileage (weekly; daily when the oil change is close). The quartermaster records oil changes. */
import { useState } from 'react';
import { api } from '../shared/api.ts';
import { Badge, Card, ErrorBox, Notice, PageHeader, Spinner, useAction, useLoad } from '../shared/ui.tsx';
import { EmptyState, when } from './common.tsx';

const mi = (n: number | null | undefined) => (n == null ? '—' : `${n.toLocaleString()} mi`);

function OilLine({ left }: { left: number | null }) {
  if (left == null) return <span className="muted">Next oil change not set — the quartermaster sets it when the oil is changed.</span>;
  if (left <= 0) return <Badge tone="bad">Oil change {Math.abs(left).toLocaleString()} mi overdue</Badge>;
  return <Badge tone={left <= 500 ? 'warn' : 'ok'}>Oil change in {left.toLocaleString()} mi</Badge>;
}

export function MileagePage() {
  const r = useLoad(() => api.get<any>('/api/me/mileage'));
  const a = useAction();
  const [miles, setMiles] = useState('');
  const [saved, setSaved] = useState<string | null>(null);
  const [big, setBig] = useState(false);
  if (!r.data) return r.error ? <ErrorBox error={r.error} /> : <Spinner />;
  const { vehicle: v, status: st, history } = r.data;
  if (!v)
    return (
      <div className="ops stack">
        <PageHeader crumb="Equipment" title="My mileage" />
        <EmptyState icon="car" title="No car is assigned to you" />
      </div>
    );
  const save = () =>
    a.run(async () => {
      const res = await api.post<any>(`/api/fleet/${v.id}/mileage`, { miles: Number(miles.replace(/\D/g, '')), confirmBig: big || undefined });
      setSaved(res.daily ? `Saved. The oil change is ${res.oilLeft <= 0 ? 'overdue' : `${res.oilLeft.toLocaleString()} miles away`} — enter the mileage every day until it’s done.` : 'Saved. See you next week.');
      setMiles('');
      setBig(false);
      r.reload();
    });
  return (
    <div className="ops stack">
      <PageHeader crumb="Equipment" title="My mileage" sub={v.label} />
      {st.due ? (
        <Notice tone={st.oilOverdue ? 'bad' : 'warn'}>{st.daily ? 'Your car is within 500 miles of an oil change, so enter the mileage every day.' : 'Time for this week’s mileage.'}</Notice>
      ) : (
        <Notice tone="ok">You’re up to date. {st.daily ? 'Next reading: tomorrow.' : `Next reading: ${new Date(+new Date(st.last) + 7 * 86_400_000).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}.`}</Notice>
      )}
      {saved && <Notice tone="ok">{saved}</Notice>}
      <Card title="Odometer">
        <form
          className="ml-form"
          onSubmit={(e) => {
            e.preventDefault();
            save();
          }}
        >
          <label>
            Mileage now
            <input inputMode="numeric" autoComplete="off" required value={miles} onChange={(e) => setMiles(e.target.value.replace(/[^\d,]/g, ''))} placeholder={v.mileage != null ? `Last: ${v.mileage.toLocaleString()}` : 'e.g. 48210'} className="ml-input" />
          </label>
          <button className="btn primary" disabled={a.busy || !miles}>
            Save mileage
          </button>
        </form>
        {a.error ? (
          <>
            <ErrorBox error={a.error} />
            {/more than the last reading/.test(String((a.error as any)?.message ?? a.error)) && (
              <label className="check">
                <input type="checkbox" checked={big} onChange={(e) => setBig(e.target.checked)} /> The number is right
              </label>
            )}
          </>
        ) : null}
        <dl className="kv" style={{ marginTop: 12 }}>
          <dt>Last reading</dt>
          <dd>
            {mi(v.mileage)} {v.mileage_at ? <span className="muted small">· {when(v.mileage_at)}</span> : null}
          </dd>
          <dt>Oil</dt>
          <dd>
            <OilLine left={st.oilLeft} />
            {v.last_oil_on ? <div className="small muted">Last changed {new Date(`${String(v.last_oil_on).slice(0, 10)}T12:00`).toLocaleDateString('en-US')} at {mi(v.last_oil_miles)}; next at {mi(v.oil_due_miles)}</div> : null}
          </dd>
        </dl>
      </Card>
      {history.length > 0 && (
        <Card title="Recent readings">
          <table className="table compact">
            <tbody>
              {history.map((h: any, i: number) => (
                <tr key={i}>
                  <td>{when(h.at)}</td>
                  <td>{mi(h.miles)}</td>
                  <td className="small muted">{h.kind === 'oil_change' ? 'Oil change' : h.kind === 'correction' ? 'Corrected' : ''} {h.by_name}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}

/** Fleet → a vehicle: oil changes and the mileage the deputy has entered. */
export function OilCard({ v, canManage, onSaved }: { v: any; canManage: boolean; onSaved: () => void }) {
  const h = useLoad(() => api.get<any>(`/api/fleet/${v.id}/mileage`).catch(() => null), [v.id, v.mileage]);
  const a = useAction();
  const [f, setF] = useState<{ miles: string; interval: string; on: string } | null>(null);
  const left = v.oil_due_miles != null && v.mileage != null ? v.oil_due_miles - v.mileage : null;
  return (
    <Card
      title="Mileage & oil"
      actions={
        canManage &&
        !f && (
          <button className="btn sm" onClick={() => setF({ miles: String(v.mileage ?? ''), interval: String(v.oil_interval ?? 5000), on: new Date().toLocaleDateString('en-CA') })}>
            Record an oil change
          </button>
        )
      }
    >
      <dl className="kv">
        <dt>Mileage</dt>
        <dd>
          {mi(v.mileage)} {v.mileage_at ? <span className="small muted">· entered {when(v.mileage_at)}</span> : null}
        </dd>
        <dt>Oil</dt>
        <dd>
          <OilLine left={left} />
          {v.last_oil_on && <div className="small muted">Last changed at {mi(v.last_oil_miles)}; every {mi(v.oil_interval)}</div>}
        </dd>
      </dl>
      {f && (
        <div className="stack sm" style={{ marginTop: 8 }}>
          <div className="grid-3">
            <label>
              Mileage at the oil change
              <input inputMode="numeric" value={f.miles} onChange={(e) => setF({ ...f, miles: e.target.value.replace(/\D/g, '') })} />
            </label>
            <label>
              Date
              <input type="date" value={f.on} onChange={(e) => setF({ ...f, on: e.target.value })} />
            </label>
            <label>
              Next one after (miles)
              <input inputMode="numeric" value={f.interval} onChange={(e) => setF({ ...f, interval: e.target.value.replace(/\D/g, '') })} />
            </label>
          </div>
          <div className="row gap-sm">
            <button className="btn primary" disabled={a.busy || !f.miles} onClick={() => a.run(async () => (await api.post(`/api/fleet/${v.id}/oil-change`, { miles: Number(f.miles), on: f.on, interval: Number(f.interval) || undefined }), setF(null), onSaved()))}>
              Save
            </button>
            <button className="btn ghost" onClick={() => setF(null)}>
              Cancel
            </button>
          </div>
          <ErrorBox error={a.error} />
        </div>
      )}
      {h.data?.history?.length > 0 && (
        <details style={{ marginTop: 8 }}>
          <summary className="small">Mileage history</summary>
          <table className="table compact">
            <tbody>
              {h.data.history.map((x: any, i: number) => (
                <tr key={i}>
                  <td>{when(x.at)}</td>
                  <td>{mi(x.miles)}</td>
                  <td className="small muted">
                    {x.kind === 'oil_change' ? 'Oil change · ' : x.kind === 'correction' ? 'Corrected · ' : ''}
                    {x.by_name}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      )}
    </Card>
  );
}
